import I18n from 'i18n-js'
import { connect } from 'react-redux'
import { ChangeEvent, FC, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import * as SimpleForm from 'builder/components/SimpleForm'
import Checkbox from 'builder/components/Checkbox'
import { TextField } from 'builder/components/TextField'
import { Recaptcha } from 'builder/components/Recaptcha'
import { DocumentTypes, FetchStatuses } from 'builder/modules/constants'
import { actions, SignUpInfo, SignUpStore } from 'builder/modules/signUp'
import { bindActionToPromise } from 'builder/utils/bindActionToPromise'
import { withMediaQueries } from 'builder/components/MediaQueries'
import SocialProfileButtons from 'builder/components/SignUp/SocialProfile/SocialProfileButtons'
import { Container } from 'builder/components/Container'

import {
  Content,
  DividerContainer,
  FieldSuggestion,
  FieldSuggestionLink,
  FieldSuggestionText,
  Line,
  OrText,
  RecaptchaContainer,
  Title,
} from './styles'

type SingleSignUpState = {
  showThankYouBlock: boolean
  emailEmptyError: boolean
}

type SingleSignInProps = {
  emailErrorCode: string
  persistSignUpInfo: ({ type, ...props }: { type: string; [x: string]: string | boolean }) => void
  email: string
  confirmEmail: boolean
  mediaQueries: {
    hover: boolean
    isPhone: boolean
    isTablet: boolean
    isLaptop: boolean
    isDesktop: boolean
  }
  setSuggestedEmail: (value: string) => void
  setEmailErrorCode: (value: string) => void
  suggestedEmail: string
  validateEmail: Awaited<({ email }: { email: string }) => any>
  isEmailSuggested: boolean
  isEmailValidating: boolean
  signUpProcess: string
  documentType: string
  isThankYouPageVisible: boolean
  onContinue: () => void
  sendMagicLink: () => void
  templateId: string
}

const SingleSignIn: FC<SingleSignInProps> = props => {
  const [componentState, setComponentState] = useState<SingleSignUpState>({
    showThankYouBlock: false,
    emailEmptyError: false,
  })
  const {
    email,
    confirmEmail,
    mediaQueries,
    suggestedEmail,
    isEmailSuggested,
    isEmailValidating,
    signUpProcess,
    isThankYouPageVisible,
    documentType,
    emailErrorCode,
    setSuggestedEmail,
    setEmailErrorCode,
    persistSignUpInfo,
    validateEmail,
    onContinue,
    templateId,
    sendMagicLink,
  } = props

  const handleSuggestionClick = () => {
    persistSignUpInfo({
      type: documentType,
      email: suggestedEmail,
    })
    setSuggestedEmail('')

    trackInternalEvent('take_suggested_sign_up_email')
  }

  const emailError = () => {
    if (componentState.emailEmptyError) return I18n.t('builder.sign_up.field_is_required')

    if (emailErrorCode) {
      if (emailErrorCode === 'TAKEN') {
        sendMagicLink()
        return ''
      }

      return I18n.t('builder.sign_up.invalid_email')
    }

    return ''
  }

  const warningText = () => {
    const dividedSuggestion = suggestedEmail.split('@')
    const emailLogin = `${dividedSuggestion[0]}@`
    const emailDomain = dividedSuggestion[1]

    return (
      <FieldSuggestion onClick={handleSuggestionClick}>
        <FieldSuggestionText>{`${I18n.t('builder.sign_up.did_you_mean')}:`}</FieldSuggestionText>
        <FieldSuggestionLink>
          {emailLogin}
          <b>{emailDomain}</b>
        </FieldSuggestionLink>
      </FieldSuggestion>
    )
  }

  const warningMarkup = suggestedEmail ? warningText() : ''

  const handleContinueClick = () => {
    if (isThankYouPageVisible && !componentState.showThankYouBlock) {
      setComponentState({ ...componentState, showThankYouBlock: true })
      trackInternalEvent('saw_thankyou_signup')
      return
    }

    trackInternalEvent('submit_sign_up_email')

    if (!email) {
      trackInternalEvent('see_sign_up_email_error', { error: 'empty_email' })
      setComponentState({ ...componentState, emailEmptyError: true })
      return
    }

    if (confirmEmail) {
      // Track marketing consent submission event
      trackInternalEvent('submit_marketing_consent', {
        website_host: window.location.host,
        device: mediaQueries.isPhone ? 'mobile' : 'desktop',
        step: 'emailstep',
      })
    }

    return validateEmail({ email }).then(() => {
      trackInternalEvent('send_sign_up_email')
      onContinue()
    })
  }

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    persistSignUpInfo({
      type: documentType,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    })

    if (e.target.name === 'email') {
      setComponentState({ ...componentState, emailEmptyError: false })
      setEmailErrorCode('')
      setSuggestedEmail('')
    }
  }

  return (
    <>
      <Container>
        <Title>{I18n.t('builder.sign_up.sign_or_create_account')}</Title>
        <Content>
          <SimpleForm.Form
            onSubmit={handleContinueClick}
            submitButtonText={
              isEmailSuggested
                ? I18n.t('builder.sign_up.confirm')
                : I18n.t('builder.sign_up.continue')
            }
            singleSignFlow={true}
            isPending={isEmailValidating || signUpProcess === FetchStatuses.loading}
          >
            {!componentState.showThankYouBlock && (
              <>
                <SimpleForm.Row style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  <TextField
                    name="email"
                    type="email"
                    label={I18n.t('builder.sign_up.email')}
                    value={email}
                    onChange={handleFieldChange}
                    error={emailError()}
                    warning={warningMarkup}
                    autoFocus={!mediaQueries.isPhone}
                    data-testid="email-input"
                    errorDataTestId="email-error"
                  />
                  <Checkbox
                    label={I18n.t('builder.sign_up.consent_checkbox_label')}
                    checked={confirmEmail}
                    onChange={handleFieldChange}
                    name="confirm_email"
                  />
                </SimpleForm.Row>
              </>
            )}
            {isThankYouPageVisible && componentState.showThankYouBlock && (
              <>
                <SimpleForm.Row>
                  <Checkbox
                    label={I18n.t('builder.sign_up.consent_checkbox_label')}
                    checked={confirmEmail}
                    onChange={handleFieldChange}
                    name="confirm_email"
                  />
                </SimpleForm.Row>
              </>
            )}
            <RecaptchaContainer>
              <Recaptcha />
            </RecaptchaContainer>
          </SimpleForm.Form>
          <DividerContainer>
            <Line />
            <OrText>{I18n.t('builder.sign_up.or')}</OrText>
            <Line />
          </DividerContainer>
          <SocialProfileButtons
            templateId={templateId}
            documentType={documentType}
            hasSpacing={false}
            singleSignFlow={true}
          />
        </Content>
      </Container>
    </>
  )
}

// ---
// Connect to data
// ---

const mapStateToProps = (state: { signUp: SignUpStore }) => ({
  emailErrorCode: state.signUp.emailErrorCode,
  email: state.signUp.signUpInfo.email,
  confirmEmail: state.signUp.signUpInfo.confirm_email,
  suggestedEmail: state.signUp.suggestedEmail,
  isEmailSuggested: state.signUp.isEmailSuggested,
  isEmailValidating: state.signUp.isEmailValidating,
  signUpProcess: state.signUp.signUpProcess,
  isThankYouPageVisible: state.signUp.isThankYouPageVisible,
})

const mapDispatchToProps = function (dispatch: any) {
  return {
    validateEmail: bindActionToPromise(dispatch, actions.validateEmail),
    setEmailErrorCode: (code: string) => dispatch(actions.setEmailErrorCode(code)),
    persistSignUpInfo: (data: { type: DocumentTypes } & Partial<SignUpInfo>) =>
      dispatch(actions.persistSignUpInfo(data)),
    setSuggestedEmail: (email: string) => dispatch(actions.setSuggestedEmail(email)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withMediaQueries(SingleSignIn))
